import { DATA_EXPORT_VERSION } from '@src/constants/version';
import type { GeneratePdfApiParams } from '@src/types/shared/api/pdf_api_params';
import uniqBy from 'lodash-es/uniqBy';

/**
 * PDF生成APIに送信するリクエストボディを、フロントエンドの状態から整形する。
 * - 重複データを除去
 * - 必須フィールドのデフォルト値を補完
 * - APIに不要なフィールド（例: id）を取り除く
 */
export function buildPdfGenerationRequest(params: GeneratePdfApiParams): Record<string, unknown> {
  const uniqueSkills = uniqBy(params.skills || [], (skill) => skill.name || '');
  const uniqueHistory = uniqBy(
    params.history || [],
    (hist) =>
      `${hist.companyName || ''}-${hist.occupation || ''}-${hist.startDate?.year || ''}-${hist.startDate?.month || ''}`
  );
  const uniqueProjects = uniqBy(
    params.project || [],
    (proj) => `${proj.title || ''}-${proj.startDate?.year || ''}-${proj.startDate?.month || ''}`
  );
  const uniqueOtherActivity = uniqBy(
    params.otherActivity || [],
    (activity) => `${activity.url || ''}-${activity.title || ''}`
  );

  return {
    version: DATA_EXPORT_VERSION,
    contact: params.contact,
    careerSummary: params.careerSummary || '',
    skillType: params.skillType || 'creator',
    bizSkills: params.bizSkills || [],
    skills: uniqueSkills.map((skill) => ({
      name: skill.name || '',
      span: skill.span || 0,
      isFreeForm: skill.isFreeForm || false,
    })),
    skillsComment: params.skillsComment || '',
    history: uniqueHistory.map((hist) => {
      const historyData: Record<string, unknown> = {
        type: hist.type || 'officeWorker',
        startDate: {
          year: hist.startDate?.year || '',
          month: hist.startDate?.month || '',
        },
        isCurrentJob: hist.isCurrentJob || false,
        companyName: hist.companyName || '',
        url: hist.url || '',
        occupation: hist.occupation || '',
        comment: hist.comment || '',
      };

      if (!hist.isCurrentJob && hist.endDate) {
        historyData.endDate = {
          year: hist.endDate.year || '',
          month: hist.endDate.month || '',
        };
      }

      return historyData;
    }),
    project: uniqueProjects.map((proj) => {
      const projectData: Record<string, unknown> = {
        title: proj.title || '',
        historyIndex: proj.historyIndex || 0,
        position: proj.position || '',
        summary: proj.summary || '',
        url: proj.url || '',
        capacity: proj.capacity || '',
        techStack: proj.techStack || '',
        startDate: {
          year: proj.startDate?.year || '',
          month: proj.startDate?.month || '',
        },
        isCurrentWork: proj.isCurrentWork || false,
      };

      if (!proj.isCurrentWork && proj.endDate) {
        projectData.endDate = {
          year: proj.endDate.year || '',
          month: proj.endDate.month || '',
        };
      }

      return projectData;
    }),
    nextCareer: params.nextCareer || '',
    educationalBackground: {
      graduationDate: {
        year: params.educationalBackground?.graduationDate?.year || '',
        month: params.educationalBackground?.graduationDate?.month || '',
      },
      institution: params.educationalBackground?.institution || '',
      major: params.educationalBackground?.major || '',
      research: {
        title: params.educationalBackground?.research?.title || '',
        description: params.educationalBackground?.research?.description || '',
      },
      finishedType: params.educationalBackground?.finishedType || 'completion',
    },
    otherActivity: uniqueOtherActivity.map((activity) => ({
      url: activity.url || '',
      title: activity.title || '',
      description: activity.description || '',
    })),
    isBlind: params.isBlind || false,
  };
}

export type PdfGenerationRequest = ReturnType<typeof buildPdfGenerationRequest>;
